import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LedgerEngine } from '../../services/finance/ledgerEngine';
import { ScreeningEngine, ScreeningEvaluationInput, ScreeningResult } from '../../services/admissions/screeningEngine';
import { API_HOST } from '../config/api';
import { useAppStore } from '../stores/useAppStore';

const API_BASE = `${API_HOST}/api`;

export interface InvoiceItem {
  id: string;
  invoiceNumber: string;
  feeTitle?: string;
  title?: string;
  category?: string;
  amountDueKobo?: number;
  amountKobo?: number;
  amountPaidKobo?: number;
  status: 'UNPAID' | 'PARTIALLY_PAID' | 'PAID';
  dueDate: string;
  paidAt?: string;
  formattedDue?: string;
  formattedPaid?: string;
}

export interface VirtualAccountData {
  bank_name: string;
  account_number: string;
  account_name: string;
  provider: string;
}

export interface HostelBedspace {
  id: string;
  name?: string;
  isOccupied: boolean;
  isReserved?: boolean;
  isAvailable?: boolean;
  status?: string;
  remainingLockSeconds?: number;
}

export interface HostelRoomData {
  roomId: string;
  hostelName: string;
  gender: string;
  roomNumber: string;
  capacity: number;
  feeKobo: number;
  bedspaces: HostelBedspace[];
}

export interface CourseResultItem {
  courseCode: string;
  code?: string;
  courseTitle: string;
  title?: string;
  creditUnits: number;
  units?: number;
  caScore?: number;
  ca?: number;
  examScore?: number;
  exam?: number;
  totalScore: number;
  total?: number;
  letterGrade: string;
  grade?: string;
  gradePoint: number;
  point?: number;
  isPass?: boolean;
}

export interface StudentResultData {
  student?: {
    matricNumber: string;
    fullName: string;
    division: string;
    programme: string;
    level: number;
    session: string;
    semester: string;
  };
  semester?: {
    totalCreditUnitsRegistered: number;
    totalCreditUnitsEarned: number;
    totalQualityPoints: number;
    gpa: number;
    courses: CourseResultItem[];
  };
  cumulative?: {
    totalCumulativeRegistered: number;
    totalCumulativeEarned: number;
    totalCumulativeQualityPoints: number;
    cgpa: number;
    academicStanding: string;
    isProbation: boolean;
  };
  approvalStatus?: string;
}

export interface WardData {
  studentId: string;
  fullName: string;
  division: string;
  programme: string;
  level: number;
  matricNumber: string;
  currentGPA?: number;
  terminalAverage?: string;
  terminalPosition?: string;
  attendanceRate: string;
  feeStatus: string;
  outstandingKobo: number;
  outstandingFormatted: string;
  virtualAccount: string;
}

export interface ParentWardsData {
  parent: {
    parentId: string;
    fullName: string;
    email: string;
    phone: string;
  };
  wards: WardData[];
}

/**
 * Fetch Student Invoices with TanStack Query
 */
export function useInvoices(studentId: string = 'std-sample-001') {
  return useQuery<InvoiceItem[]>({
    queryKey: ['invoices', studentId],
    queryFn: async () => {
      try {
        const res = await fetch(`${API_BASE}/finance/invoices`);
        if (!res.ok) throw new Error(`HTTP error ${res.status}`);
        const data: any = await res.json();
        return data.invoices as InvoiceItem[];
      } catch (err) {
        // Fallback demo data if backend offline
        return [
          {
            id: 'inv-001',
            invoiceNumber: 'INV-2026-COEKA-00184',
            feeTitle: '2026/2027 NCE Tuition & Consolidated Institutional Fees',
            category: 'TUITION',
            amountDueKobo: 4500000,
            amountPaidKobo: 0,
            status: 'UNPAID',
            dueDate: '2026-12-15',
            formattedDue: LedgerEngine.koboToNaira(4500000),
            formattedPaid: LedgerEngine.koboToNaira(0),
          },
          {
            id: 'inv-002',
            invoiceNumber: 'INV-2026-COEKA-00185',
            feeTitle: 'Hostel Accommodation (Hall A - Female Bedspace)',
            category: 'HOSTEL',
            amountDueKobo: 2000000,
            amountPaidKobo: 2000000,
            status: 'PAID',
            dueDate: '2026-11-30',
            paidAt: '2026-10-05T14:32:00Z',
            formattedDue: LedgerEngine.koboToNaira(2000000),
            formattedPaid: LedgerEngine.koboToNaira(2000000),
          },
        ];
      }
    },
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Fetch Student Dedicated Virtual Account
 */
export function useVirtualAccount(studentId: string = 'std-sample-001') {
  return useQuery<VirtualAccountData>({
    queryKey: ['virtualAccount', studentId],
    queryFn: async () => {
      try {
        const res = await fetch(`${API_BASE}/finance/virtual-account`);
        if (!res.ok) throw new Error(`HTTP error ${res.status}`);
        const data: any = await res.json();
        return data.virtualAccount as VirtualAccountData;
      } catch (err) {
        return {
          bank_name: 'Wema Bank (VPay / COEKA)',
          account_number: '9910840184',
          account_name: 'COEKA - AONDOAVER MOSES IORLIAM',
          provider: 'VPAY',
        };
      }
    },
    staleTime: 1000 * 60 * 30,
  });
}

/**
 * Fetch Hostel Rooms and Bedspace Real-Time Status
 */
export function useHostelRooms() {
  return useQuery<HostelRoomData[]>({
    queryKey: ['hostelRooms'],
    queryFn: async () => {
      try {
        const res = await fetch(`${API_BASE}/hostels/rooms`);
        if (!res.ok) throw new Error(`HTTP error ${res.status}`);
        const data: any = await res.json();
        return data.rooms as HostelRoomData[];
      } catch (err) {
        return [
          {
            roomId: 'room-a-101',
            hostelName: 'Hall A (Queen Amina Hall - Female)',
            gender: 'FEMALE',
            roomNumber: 'Room 101 (Ground Floor)',
            capacity: 4,
            feeKobo: 2000000,
            bedspaces: [
              { id: 'bed-a101-1', isOccupied: false, isReserved: false, isAvailable: true, remainingLockSeconds: 0 },
              { id: 'bed-a101-2', isOccupied: false, isReserved: false, isAvailable: true, remainingLockSeconds: 0 },
              { id: 'bed-a101-3', isOccupied: true, isReserved: false, isAvailable: false, remainingLockSeconds: 0 },
              { id: 'bed-a101-4', isOccupied: false, isReserved: false, isAvailable: true, remainingLockSeconds: 0 },
            ],
          },
        ];
      }
    },
    refetchInterval: 15000,
  });
}

/**
 * Mutation: Reserve a Bedspace with 15-Minute Dynamic Lock
 */
export function useReserveBedspace() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (bedspaceId: string) => {
      try {
        const res = await fetch(`${API_BASE}/hostels/reserve`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ bedspaceId }),
        });
        const data: any = await res.json();
        if (!res.ok) {
          throw new Error(data.error || 'Failed to acquire reservation lock');
        }
        return data;
      } catch (err: any) {
        // Fallback simulated lock
        return {
          message: 'Bedspace lock acquired successfully. You have 15 minutes to complete payment.',
          bedspaceId,
          reservedUntil: Math.floor(Date.now() / 1000) + 900,
          lockDurationSeconds: 900,
        };
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hostelRooms'] });
    },
  });
}

/**
 * Fetch Student Semester Result & CGPA Breakdown
 */
export function useStudentResult(division: string = 'NCE') {
  return useQuery<StudentResultData>({
    queryKey: ['studentResult', division],
    queryFn: async () => {
      try {
        const res = await fetch(`${API_BASE}/results/student-result`);
        if (!res.ok) throw new Error(`HTTP error ${res.status}`);
        const data: any = await res.json();
        return data as StudentResultData;
      } catch (err) {
        return {
          student: {
            matricNumber: 'COEKA/2026/NCE/084',
            fullName: 'Aondoaver Moses Iorliam',
            division: 'NCE',
            programme: 'NCE Computer Science / Mathematics',
            level: 100,
            session: '2026/2027',
            semester: 'First Semester',
          },
          semester: {
            totalCreditUnitsRegistered: 12,
            totalCreditUnitsEarned: 12,
            totalQualityPoints: 58,
            gpa: 4.83,
            courses: [
              { courseCode: 'CSC 111', courseTitle: 'Introduction to Computer Systems', creditUnits: 2, totalScore: 86, letterGrade: 'A', gradePoint: 5, isPass: true },
              { courseCode: 'CSC 112', courseTitle: 'Problem Solving & BASIC Programming', creditUnits: 3, totalScore: 78, letterGrade: 'A', gradePoint: 5, isPass: true },
              { courseCode: 'MTH 111', courseTitle: 'Algebra and Trigonometry', creditUnits: 3, totalScore: 70, letterGrade: 'A', gradePoint: 5, isPass: true },
              { courseCode: 'EDU 111', courseTitle: 'Philosophy of Education', creditUnits: 2, totalScore: 80, letterGrade: 'A', gradePoint: 5, isPass: true },
              { courseCode: 'GSE 111', courseTitle: 'General English I', creditUnits: 2, totalScore: 78, letterGrade: 'A', gradePoint: 5, isPass: true },
            ],
          },
          cumulative: {
            totalCumulativeRegistered: 12,
            totalCumulativeEarned: 12,
            totalCumulativeQualityPoints: 58,
            cgpa: 4.83,
            academicStanding: 'Distinction / First Class Standard',
            isProbation: false,
          },
          approvalStatus: 'SENATE_APPROVED',
        };
      }
    },
  });
}

/**
 * Fetch Multi-Ward Telemetry for Parent Portal
 */
export function useParentWards() {
  return useQuery<ParentWardsData>({
    queryKey: ['parentWards'],
    queryFn: async () => {
      try {
        const res = await fetch(`${API_BASE}/parent/wards`);
        if (!res.ok) throw new Error(`HTTP error ${res.status}`);
        const data: any = await res.json();
        return data as ParentWardsData;
      } catch (err) {
        return {
          parent: {
            parentId: 'prt-001',
            fullName: 'Mr. Joshua T. Tsegha',
            email: 'j.tsegha@gmail.com',
            phone: '08064377594',
          },
          wards: [
            {
              studentId: 'std-001',
              fullName: 'Aondoaver Moses Iorliam',
              division: 'NCE',
              programme: 'NCE Computer Science / Mathematics',
              level: 100,
              matricNumber: 'COEKA/2026/NCE/084',
              currentGPA: 4.83,
              attendanceRate: '96%',
              feeStatus: 'UNPAID',
              outstandingKobo: 4500000,
              outstandingFormatted: LedgerEngine.koboToNaira(4500000),
              virtualAccount: '9910840184 (Wema Bank)',
            },
            {
              studentId: 'std-002',
              fullName: 'Ngodoo Blessing Tsegha',
              division: 'SECONDARY',
              programme: 'Demonstration Secondary School (SS2 Science)',
              level: 200,
              matricNumber: 'COEKA/DSS/2024/042',
              terminalAverage: '78.5%',
              terminalPosition: '3rd of 45',
              attendanceRate: '98%',
              feeStatus: 'PAID',
              outstandingKobo: 0,
              outstandingFormatted: LedgerEngine.koboToNaira(0),
              virtualAccount: '9910840185 (Wema Bank)',
            },
            {
              studentId: 'std-003',
              fullName: 'Terhide Kelvin Tsegha',
              division: 'PRIMARY',
              programme: 'Staff Primary School (Basic 4)',
              level: 4,
              matricNumber: 'COEKA/SPS/2022/019',
              terminalAverage: '84.0%',
              terminalPosition: '1st of 32',
              attendanceRate: '100%',
              feeStatus: 'PAID',
              outstandingKobo: 0,
              outstandingFormatted: LedgerEngine.koboToNaira(0),
              virtualAccount: '9910840186 (Wema Bank)',
            },
          ],
        };
      }
    },
  });
}

/**
 * Mutation: Admissions UTME/O-Level Screening
 */
export function useAdmissionsScreening() {
  return useMutation({
    mutationFn: async (input: ScreeningEvaluationInput): Promise<ScreeningResult> => {
      return ScreeningEngine.evaluateApplication(input);
    },
  });
}

export interface ScopedSearchResultItem {
  id: string;
  title: string;
  subtitle?: string;
  type: 'STUDENT' | 'COURSE' | 'INVOICE' | 'STAFF' | 'FINANCE';
  tab: string;
  url?: string;
  metadata?: Record<string, any>;
}

/**
 * Hook to perform real-time Scoped Search querying /api/search
 */
export function useScopedSearch(query: string, enabled: boolean = true) {
  const { userSession } = useAppStore();
  const token = userSession?.token;
  const role = userSession?.role;
  const division = userSession?.division;

  return useQuery<ScopedSearchResultItem[]>({
    queryKey: ['scoped-search', query, role, userSession?.userId],
    queryFn: async () => {
      const trimmed = query.trim();
      if (!trimmed) return [];

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      if (role) {
        headers['X-Demo-Role'] = role;
      }
      if (division) {
        headers['X-Demo-Division'] = division;
      }

      try {
        const res = await fetch(`${API_BASE}/search?q=${encodeURIComponent(trimmed)}`, {
          headers,
          credentials: 'include',
        });
        if (!res.ok) return [];
        const data = (await res.json()) as { results?: ScopedSearchResultItem[] };
        return (data.results || []) as ScopedSearchResultItem[];
      } catch {
        return [];
      }
    },
    enabled: enabled && query.trim().length > 0,
    staleTime: 1000 * 30,
  });
}

